/**
 * Auth_Service.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SK Hire owns NO authentication. Login / signup / forgot / reset / verify all
 * live on the SK Platform. This service holds only the session-plumbing the
 * product legitimately needs against the shared platform session:
 *
 *   • getCurrentUser() — GET /v1/me (identity probe; gates the app shell)
 *   • logoutUser()     — POST /v1/auth/logout (clears the shared session)
 *
 * Cookies (accessToken + refreshToken) are httpOnly — never touched here.
 * Only non-sensitive UI data (orgId, display name) is cached via Token_Handler.
 */

import axiosInstance from '../../api/axiosInstance';
import { Token_Handler } from '../../storage/tokenHandler';
import { Platform_Me_Url, Platform_Logout_Url } from '../../api/endpoints';

/* ─────────────────────────────────────────
   Me — GET /v1/me
   Module-level cache (5s TTL) + in-flight deduplicator so parallel callers
   (rootNavigator gate, sessionContext) share one request.
───────────────────────────────────────── */

const ME_TTL = 5_000;
let _meCache: { data: any; at: number } | null = null;
let _meInflight: Promise<any> | null = null;

export const invalidateMeCache = () => { _meCache = null; };

export const getCurrentUser = async (): Promise<any> => {
  if (_meCache && Date.now() - _meCache.at < ME_TTL) return _meCache.data;
  if (!_meInflight) {
    _meInflight = axiosInstance
      .get(Platform_Me_Url)
      .then(r => { _meCache = { data: r.data, at: Date.now() }; return r.data; })
      .finally(() => { _meInflight = null; });
  }
  return _meInflight;
};

/* ─────────────────────────────────────────
   Logout — POST /v1/auth/logout
   Best-effort: always clear local UI cache even if the network call fails.
───────────────────────────────────────── */

export const logoutUser = async (): Promise<void> => {
  try {
    await axiosInstance.post(Platform_Logout_Url);
  } finally {
    invalidateMeCache();
    await Token_Handler.clearAll();
  }
};
